import { useState, useEffect } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';

// Interfaz de configuración del sistema
export interface SystemConfig {
    // Modo mantenimiento
    maintenanceMode: boolean;
    maintenanceMessage: string;

    // Planes
    plans: {
        free: {
            name: string;
            maxUsers: number;
            maxInstructors: number;
            features: string[];
            hasAdvancedStats: boolean;
            maxRooms: number;
            hasCommunications: boolean;
            hasOnlinePayments: boolean;
        };
        basic: {
            name: string;
            price: number;
            maxUsers: number;
            maxInstructors: number;
            features: string[];
            hasAdvancedStats: boolean;
            maxRooms: number;
            hasCommunications: boolean;
            hasOnlinePayments: boolean;
        };
        pro: {
            name: string;
            price: number;
            maxUsers: number;
            maxInstructors: number;
            features: string[];
            hasAdvancedStats: boolean;
            maxRooms: number;
            hasCommunications: boolean;
            hasOnlinePayments: boolean;
        };
    };

    // Stripe
    platformFeePercent: number;

    // General
    supportEmail: string;
    defaultLanguage: string;
    defaultTimezone: string;
}

// Valores por defecto (usados si no hay configuración en Firestore)
export const DEFAULT_SYSTEM_CONFIG: SystemConfig = {
    maintenanceMode: false,
    maintenanceMessage: 'Estamos realizando tareas de mantenimiento. Volvemos pronto.',
    plans: {
        free: {
            name: 'Gratis',
            maxUsers: 50,
            maxInstructors: 1,
            features: ['Hasta 50 usuarios', '1 instructor', 'Funciones básicas'],
            hasAdvancedStats: false,
            maxRooms: 1,
            hasCommunications: false,
            hasOnlinePayments: false
        },
        basic: {
            name: 'Básico',
            price: 19.90,
            maxUsers: 200,
            maxInstructors: 5,
            features: ['Hasta 200 usuarios', '5 instructores', 'Estadísticas básicas', 'Soporte por email'],
            hasAdvancedStats: false,
            maxRooms: 2,
            hasCommunications: true,
            hasOnlinePayments: true
        },
        pro: {
            name: 'Profesional',
            price: 34.90,
            maxUsers: -1, // Ilimitado
            maxInstructors: -1,
            features: ['Usuarios ilimitados', 'Instructores ilimitados', 'Estadísticas avanzadas', 'Soporte prioritario', 'Personalización avanzada'],
            hasAdvancedStats: true,  // Solo Pro tiene estadísticas
            maxRooms: -1,
            hasCommunications: true,
            hasOnlinePayments: true
        }
    },
    platformFeePercent: 5,
    supportEmail: 'navora.citas@gmail.com',
    defaultLanguage: 'es',
    defaultTimezone: 'Europe/Madrid'
};

// Función para hacer merge profundo de objetos
function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
    const result = { ...target };

    for (const key in source) {
        if (source.hasOwnProperty(key)) {
            const sourceValue = source[key];
            const targetValue = result[key];

            if (
                sourceValue !== null &&
                typeof sourceValue === 'object' &&
                !Array.isArray(sourceValue) &&
                targetValue !== null &&
                typeof targetValue === 'object' &&
                !Array.isArray(targetValue)
            ) {
                // Merge recursivo para objetos anidados
                result[key] = deepMerge(targetValue, sourceValue);
            } else if (sourceValue !== undefined) {
                // Usa el valor de source si existe
                result[key] = sourceValue as T[typeof key];
            }
        }
    }

    return result;
}

/**
 * Hook para obtener la configuración global del sistema
 * Lee de 'system/config' en Firestore con fallback a valores por defecto
 * 
 * @param realtime - Si true, usa onSnapshot para actualizaciones en tiempo real
 */
export function useSystemConfig(realtime: boolean = false) {
    const [config, setConfig] = useState<SystemConfig>(DEFAULT_SYSTEM_CONFIG);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const docRef = doc(db, 'system', 'config');

        if (realtime) {
            // Suscripción en tiempo real
            const unsubscribe = onSnapshot(
                docRef,
                (snapshot) => {
                    if (snapshot.exists()) {
                        setConfig(deepMerge(DEFAULT_SYSTEM_CONFIG, snapshot.data() as Partial<SystemConfig>));
                    } else {
                        setConfig(DEFAULT_SYSTEM_CONFIG);
                    }
                    setLoading(false);
                },
                (err) => {
                    console.error('Error cargando configuración del sistema:', err);
                    setError(err);
                    setLoading(false);
                }
            );

            return () => unsubscribe();
        } else {
            // Lectura única
            const loadConfig = async () => {
                try {
                    const snapshot = await getDoc(docRef);
                    if (snapshot.exists()) {
                        setConfig(deepMerge(DEFAULT_SYSTEM_CONFIG, snapshot.data() as Partial<SystemConfig>));
                    } else {
                        setConfig(DEFAULT_SYSTEM_CONFIG);
                    }
                } catch (err) {
                    console.error('Error cargando configuración del sistema:', err);
                    setError(err as Error);
                } finally {
                    setLoading(false);
                }
            };

            loadConfig();
        }
    }, [realtime]);

    return { config, loading, error };
}

/**
 * Función helper para obtener configuración una sola vez (para uso en funciones)
 */
export async function getSystemConfig(): Promise<SystemConfig> {
    try {
        const docRef = doc(db, 'system', 'config');
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
            return deepMerge(DEFAULT_SYSTEM_CONFIG, snapshot.data() as Partial<SystemConfig>);
        }

        return DEFAULT_SYSTEM_CONFIG;
    } catch (error) {
        console.error('Error obteniendo configuración del sistema:', error);
        return DEFAULT_SYSTEM_CONFIG;
    }
}
